import { DoubleRightOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { DatePicker, InputNumber, message, Modal, ModalProps, Tooltip, Typography } from 'antd';
import { AxiosResponse } from 'axios';
import moment, { Moment } from 'moment';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../api/useAPIMutation';
import useAPIQuery from '../api/useAPIQuery';
import useInfiniteAPIQuery from '../api/useInfiniteAPIQuery';
import usePrefetchPages from '../hooks/usePrefetchPages';
import { CandidateDetail, JobPostingDetail } from '../types';
import {
  currencyFormatter,
  mapVixtingCompany,
  mapVixtingDepartment,
  mapVixtingJobRole,
  currencyParser,
} from '../utils';
import Form from './Form';
import { MissingAdmissionDataAlert } from './MoveCandidateStageModal';
import VixtingAdmissionForm from './VixtingAdmissionForm';

interface SendVixtingAdmissionFormValues {
  admissionDate: Moment;
  salary: number;
  candidateEmail: string;
  candidateCPF: string;
  vixtingJobRole: string;
  vixtingCompany: string;
  vixtingDepartment: string;
  affiliate: string;
  unitBusiness: string;
  admissionType: string;
  vixtingLimitDate: Moment;
}

interface SendVixtingAdmissionModalProps extends ModalProps {
  jobPostingId: JobPostingDetail['id'];
  candidateId?: CandidateDetail['id'];
  onSuccess?: (
    data: AxiosResponse<unknown, unknown> | void,
    variables: SendVixtingAdmissionFormValues,
    context: unknown
  ) => void | Promise<unknown>;
}

const STALE_TIME = 1000 * 60 * 5;

const SendVixtingAdmissionModal = ({
  jobPostingId,
  candidateId,
  onSuccess,
  ...props
}: SendVixtingAdmissionModalProps) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const config = useAPIQuery('/configs/tenant-configurations/');

  const candidate = useAPIQuery([`/candidates/${candidateId}/`], { enabled: !!candidateId });

  const jobPosting = useAPIQuery([`/job-postings/${jobPostingId}/`]);

  const recruitmentProcess = useAPIQuery(
    [
      '/recruitment-processes/',
      {
        params: {
          candidate: candidateId,
          job_posting: jobPostingId,
        },
      },
    ],
    {
      enabled: !!candidateId,
      select(data) {
        return data.results[0];
      },
    }
  );

  const vixtingJobPosting = useAPIQuery(
    [
      '/vixting/vixting-job-postings/',
      {
        params: {
          job_posting_id: jobPostingId,
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission,
      select(data) {
        return data.count > 0 ? data.results[0] : undefined;
      },
    }
  );

  const vixtingCompaniesPages = useInfiniteAPIQuery(
    [
      '/vixting/vixting-companies/',
      {
        params: {
          is_active: true,
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission,
      staleTime: STALE_TIME,
      onError: () => {
        message.error(t('Falha ao carregar empresas!'));
      },
    }
  );
  const vixtingCompanies = usePrefetchPages(vixtingCompaniesPages, (data) => data.results);

  const vixtingJobRolesPages = useInfiniteAPIQuery(
    [
      '/vixting/job-roles/',
      {
        params: {
          company_id: vixtingJobPosting.data?.company || '',
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission && !!vixtingJobPosting.data?.company,
      staleTime: STALE_TIME,
      onError: () => {
        message.error(t('Falha ao carregar cargos da vixting!'));
      },
      pageKey: 'offsetPage',
    }
  );
  const vixtingJobRoles = usePrefetchPages(vixtingJobRolesPages, (data) => data.results);

  const vixtingDepartmentsPages = useInfiniteAPIQuery(
    [
      '/vixting/departments/',
      {
        params: {
          company_id: vixtingJobPosting.data?.company || '',
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission && !!vixtingJobPosting.data?.company,
      staleTime: STALE_TIME,
      onError: () => {
        message.error(t('Falha ao carregar departamentos da vixting!'));
      },
      pageKey: 'offsetPage',
    }
  );
  const vixtingDepartments = usePrefetchPages(vixtingDepartmentsPages, (data) => data.results);

  const vixtingAffiliatesPages = useInfiniteAPIQuery(
    [
      '/vixting/affiliates/',
      {
        params: {
          company_id: vixtingJobPosting.data?.company || '',
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission && !!vixtingJobPosting.data?.company,
      staleTime: STALE_TIME,
      onSuccess: () => {
        if (!vixtingJobPosting.data?.affiliate && !!vixtingJobPosting.data?.company)
          form.setFieldValue('affiliate', vixtingJobPosting.data.company);
      },
      onError: () => {
        message.error(t('Falha ao carregar filiais da vixting!'));
      },
      pageKey: 'offsetPage',
    }
  );

  const vixtingAffiliates = usePrefetchPages(vixtingAffiliatesPages, (data) => data.results);

  const vixtingUnitBusinessesPages = useInfiniteAPIQuery(
    [
      '/vixting/unit-businesses/',
      {
        params: {
          company_id: vixtingJobPosting.data?.company || '',
        },
      },
    ],
    {
      enabled:
        !!config.data?.enableVixtingAdmission &&
        !!config.data?.enableVixtingUnitBusiness &&
        !!vixtingJobPosting.data?.company,
      staleTime: STALE_TIME,
      onError: () => {
        message.error(t('Falha ao carregar unidades de negócio da vixting!'));
      },
      pageKey: 'offsetPage',
    }
  );

  const vixtingUnitBusinesses = usePrefetchPages(
    vixtingUnitBusinessesPages,
    (data) => data.results
  );

  const vixtingAdmission = useAPIQuery(
    [
      '/vixting/vixting-admissions/',
      {
        params: {
          recruitment_process: recruitmentProcess.data?.id,
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission && !!recruitmentProcess.data?.id,
      staleTime: STALE_TIME,
      select(data) {
        return data.results[0];
      },
      onError: () => {
        message.error(t('Falha ao carregar admissão da vixting!'));
      },
    }
  );

  const startSalaryRange = jobPosting.data?.start_salary_range;
  const endSalaryRange = jobPosting.data?.end_salary_range;

  const isVixtingFormAndIsMissingData =
    config.data?.enableVixtingAdmission &&
    (!vixtingJobPosting.data?.company ||
      !vixtingJobPosting.data?.job_role ||
      !vixtingJobPosting.data?.department ||
      !vixtingJobPosting.data?.affiliate);

  const mutation = useAPIMutation(
    async ({ salary, admissionType, vixtingLimitDate }: SendVixtingAdmissionFormValues, api) => {
      const recruitmentProcesses = await api.get('/recruitment-processes/', {
        params: {
          job_posting: jobPostingId,
          candidate: candidateId,
          status: 'ACTIVE',
        },
      });

      const recruitmentProcess =
        recruitmentProcesses.data.count > 0 ? recruitmentProcesses.data.results[0] : null;

      if (recruitmentProcess) {
        await api.patch(`/recruitment-processes/${recruitmentProcess.id}/`, {
          salary,
        });

        await api.post('/vixting/vixting-admissions/', {
          recruitment_process: recruitmentProcess.id,
          admission_type: admissionType,
          limit_date: vixtingLimitDate.format('YYYY-MM-DD'),
        });
      }
    },
    {
      onSettled: async () => {
        await queryClient.invalidateQueries('/vixting/vixting-admissions/');
      },
      onSuccess: async (data, variables, context) => {
        message.success(t('Admissão enviada.'));

        onSuccess && onSuccess(data, variables, context);
      },
    }
  );

  const getVixtingLimitDate = () => {
    return !!recruitmentProcess.data?.admission_date &&
      config.data?.vixtingAdmissionDateReference === 'admission_date'
      ? moment(recruitmentProcess.data?.admission_date)
          .subtract(config.data?.vixtingDaysToAdmission, 'days')
          .startOf('day')
      : moment().add(config.data?.vixtingDaysToAdmission, 'days').startOf('day');
  };

  const defaultCandidatePhone =
    candidate.data && !!candidate.data.phone_contacts[0]
      ? candidate.data.phone_contacts[0].number
      : 'NA';

  const formArgs = {
    sankhyaCompanies: mapVixtingCompany(vixtingCompanies),
    sankhyaJobRoles: mapVixtingJobRole(vixtingJobRoles),
    sankhyaDepartments: mapVixtingDepartment(vixtingDepartments),
    affiliates: vixtingAffiliates,
    unitBusinesses: vixtingUnitBusinesses,
  };

  const isVacancyCompanyActiveUsable = vixtingCompanies.some(
    (company) => company.id === vixtingJobPosting.data?.company && company.is_active
  );

  return (
    <Modal
      okButtonProps={{
        htmlType: 'submit',
        icon: <DoubleRightOutlined />,
        loading: mutation.isLoading,
        title: !isVacancyCompanyActiveUsable
          ? t(
              'Não é possível prosseguir com o envio da admissão.\nA empresa associada à vaga não está configurada corretamente.'
            )
          : undefined,
        disabled: !isVacancyCompanyActiveUsable || isVixtingFormAndIsMissingData,
      }}
      okText={t('Enviar')}
      onOk={async () => form.submit()}
      title={
        <Typography.Text>
          <Trans>Enviar admissão</Trans>:{' '}
          <Typography.Text strong>{candidate.data?.name}</Typography.Text>
        </Typography.Text>
      }
      {...props}
    >
      <Helmet>
        <title>
          {`${t('Enviar admissão')} ${candidate.data?.name} | ${t('Recrutamento e Seleção')}`}
        </title>
      </Helmet>
      <Form<SendVixtingAdmissionFormValues>
        form={form}
        disabled={isVixtingFormAndIsMissingData}
        layout="vertical"
        initialValues={{
          admissionDate: moment(recruitmentProcess.data?.admission_date),
          salary: recruitmentProcess.data?.salary,
          candidateEmail: candidate.data?.email || '',
          candidateCPF: candidate.data?.cpf || '',
          sankhyaCompany: vixtingJobPosting.data?.company,
          sankhyaJobRole: vixtingJobPosting.data?.job_role,
          sankhyaDepartment: vixtingJobPosting.data?.department,
          affiliate: vixtingJobPosting.data?.affiliate,
          unitBusiness: vixtingJobPosting.data?.unit_business,
          vixtingLimitDate: vixtingAdmission.data?.limit_date
            ? moment(vixtingAdmission.data?.limit_date)
            : getVixtingLimitDate(),
          admissionType: vixtingAdmission.data?.admission_type,
          candidatePhone: defaultCandidatePhone,
        }}
        onFinish={async (values) => {
          mutation.mutate(values);
        }}
      >
        {isVixtingFormAndIsMissingData && <MissingAdmissionDataAlert jobPostingId={jobPostingId} />}
        <Form.Row>
          <Form.Col span={12}>
            <Form.Item
              label={t('Data de admissão')}
              name="admissionDate"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <DatePicker
                disabledDate={(current) => {
                  // Days before today are not allowed
                  return current && current < moment().startOf('day');
                }}
                style={{ width: '100%' }}
                disabled={!!recruitmentProcess.data?.admission_date}
              />
            </Form.Item>
          </Form.Col>
          <Form.Col span={12}>
            <Form.Item
              label={
                <Typography.Text>
                  <Trans>Salário</Trans>{' '}
                  <Tooltip
                    title={
                      endSalaryRange
                        ? startSalaryRange
                          ? `${t('Faixa salarial definida entre R$')} ${currencyFormatter(
                              currencyFormatter(startSalaryRange)
                            )} ${t('e R$')} ${currencyFormatter(endSalaryRange)}`
                          : `${t('Salário máximo definido de R$')} ${currencyFormatter(
                              endSalaryRange
                            )}`
                        : t('Vaga sem informação de Faixa Salarial')
                    }
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </Typography.Text>
              }
              name="salary"
              rules={[
                {
                  required: !!endSalaryRange,
                },
                {
                  validator(rule, value) {
                    if (value && endSalaryRange) {
                      if (
                        startSalaryRange &&
                        (value > endSalaryRange || value < startSalaryRange)
                      ) {
                        return Promise.reject(
                          new Error(
                            t(
                              `O salário deve estar entre R$ {{startSalaryRange}} e R$ {{endSalaryRange}}`,
                              {
                                startSalaryRange: currencyFormatter(startSalaryRange),
                                endSalaryRange: currencyFormatter(endSalaryRange),
                              }
                            )
                          )
                        );
                      }
                      if (value > endSalaryRange) {
                        return Promise.reject(
                          new Error(
                            t(`O salário deve ser menor que R$ {{endSalaryRange}}`, {
                              endSalaryRange: currencyFormatter(endSalaryRange),
                            })
                          )
                        );
                      }
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber<string | number>
                prefix={t('R$')}
                style={{ width: '100%' }}
                step={100}
                min={0}
                max={2147483647}
                formatter={(value) => currencyFormatter(value)}
                parser={(displayValue) => currencyParser(displayValue)}
                disabled={!!recruitmentProcess.data?.salary}
              />
            </Form.Item>
          </Form.Col>
        </Form.Row>
        <VixtingAdmissionForm {...formArgs} />
      </Form>
    </Modal>
  );
};

export default SendVixtingAdmissionModal;
